import { createAsyncThunk } from '@reduxjs/toolkit';
import { actionTypes } from 'src/redesign/features/Events/store/constants';
import {
  copyEventApi,
  createAIEventApi,
  createDateWithTimesEventOccurrenceApi,
  createEventApi,
  createEventOccurrenceApi,
  createEventPromosApi,
  createEventTicketsApi,
  createPreapprovedEventAPI,
  deleteDateWithTimesEventOccurrenceApi,
  deleteEventApi,
  deleteEventPromoApi,
  deleteEventTicketsApi,
  fetchEventApi,
  fetchEventLeafOccurrencesApi,
  fetchEventOccurrencesApi,
  fetchEventPromosApi,
  fetchEventsApi,
  fetchEventsBySiteApi,
  fetchEventTicketsApi,
  fetchEventTicketsWithoutMetricsApi,
  generateAITextApi,
  notifyWaitlistSubscribersApi,
  submitPreapprovedEventAPI,
  updateDateWithTimesEventOccurrenceApi,
  updateEventApi,
  updateEventOccurrenceApi,
  updateEventPromosApi,
  updateEventTicketsApi,
  updateEventTicketsOrderApi,
  updateEventWaitlistApi,
  fetchEventHostsApi,
  createEventHostApi,
  updateEventHostApi,
  deleteEventHostApi,
  inviteTeamMemberAsHostApi,
  addEventHostDirectApi,
  resendPendingHostInvitationApi,
  deletePendingHostInvitationApi,
  bulkUpdateBookingFeeApi,
} from 'src/redesign/features/Events/store/api';
import { Event, Ticket } from 'src/types/types';
import { withErrorHandling } from 'src/utils/async-thunk-error-handler';

export const fetchEvents = createAsyncThunk(
  actionTypes.FETCH_EVENTS,
  async (params?: {
    limit?: number;
    offset?: number;
    status?: string[];
    organization_ids?: number[];
    skip_occurrences?: boolean;
  }) => {
    return await fetchEventsApi(params);
  },
);

export const fetchMoreEvents = createAsyncThunk(
  actionTypes.FETCH_MORE_EVENTS,
  async (params?: {
    limit?: number;
    offset?: number;
    status?: string[];
    organization_ids?: number[];
    skip_occurrences?: boolean;
  }) => {
    return await fetchEventsApi(params);
  },
);

export const fetchEventsBySite = createAsyncThunk(
  actionTypes.FETCH_EVENTS_BY_SITE,
  async (siteId: string) => await fetchEventsBySiteApi(siteId),
);

export const fetchEventById = createAsyncThunk(
  actionTypes.FETCH_EVENT_BY_ID,
  withErrorHandling((params: { eventId: string; skipOccurrences?: boolean } | string) => {
    const eventId = typeof params === 'string' ? params : params.eventId;
    const skipOccurrences = typeof params === 'string' ? true : (params.skipOccurrences ?? true);
    return fetchEventApi(eventId, skipOccurrences);
  }, 'Error fetching event'),
);
export const fetchEventByIdWithoutLoading = createAsyncThunk(
  actionTypes.FETCH_EVENT_BY_ID_LOADING,
  withErrorHandling((params: { eventId: string; skipOccurrences?: boolean } | string) => {
    const eventId = typeof params === 'string' ? params : params.eventId;
    const skipOccurrences = typeof params === 'string' ? true : (params.skipOccurrences ?? true);
    return fetchEventApi(eventId, skipOccurrences);
  }, 'Error fetching event'),
);

export const updateEvent = createAsyncThunk(
  actionTypes.UPDATE_EVENT,
  async (event: Event & { operationType?: 'save' | 'publish' }, { rejectWithValue }) => {
    try {
      const data = await updateEventApi(event);
      return data;
    } catch (error) {
      console.error('Error updating event:', error);
      const message = error.response?.data?.error
        ? { message: error.response?.data?.error }
        : error.response?.data?.details;
      return rejectWithValue(message);
    }
  },
);

export const createEvent = createAsyncThunk(
  actionTypes.CREATE_EVENT,
  withErrorHandling((event: Event) => createEventApi(event), 'Error creating event'),
);

export const deleteEvent = createAsyncThunk(
  actionTypes.DELETE_EVENT,
  async (eventId: string) => await deleteEventApi(eventId),
);

export const copyEvent = createAsyncThunk(
  actionTypes.COPY_EVENT,
  async (data: { id: string; checkboxSelections: any }) => await copyEventApi(data),
);

export const createAIEvent = createAsyncThunk(
  actionTypes.CREATE_AI_EVENT,
  async (event: Event) => await createAIEventApi(event),
);

export const generateAIText = createAsyncThunk(
  actionTypes.GENERATE_AI_TEXT,
  async (data: any) => await generateAITextApi(data),
);

export const fetchEventTickets = createAsyncThunk(
  actionTypes.FETCH_EVENT_TICKETS,
  async (eventId: string) => await fetchEventTicketsApi(eventId),
);

export const getEventTicketsWithoutMetrics = createAsyncThunk(
  actionTypes.FETCH_EVENT_TICKETS_WITHOUT_METRICS,
  async (eventId: string) => await fetchEventTicketsWithoutMetricsApi(eventId),
);

export const createEventTickets = createAsyncThunk(
  actionTypes.CREATE_EVENT_TICKET,
  async (ticket: Ticket) => await createEventTicketsApi(ticket),
);

export const updateEventTickets = createAsyncThunk(
  actionTypes.UPDATE_EVENT_TICKET,
  async (ticket: Ticket) => await updateEventTicketsApi(ticket),
);

export const updateEventTicketsOrder = createAsyncThunk(
  actionTypes.UPDATE_EVENT_TICKETS_ORDER,
  async (ticket: Ticket) => await updateEventTicketsOrderApi(ticket),
);

export const deleteEventTickets = createAsyncThunk(
  actionTypes.DELETE_EVENT_TICKET,
  async (id: string) => await deleteEventTicketsApi(id),
);

export const fetchEventOccurrences = createAsyncThunk(
  actionTypes.FETCH_EVENT_OCCURRENCES,
  async (event: Event) => await fetchEventOccurrencesApi(event),
);

export const fetchEventLeafOccurrences = createAsyncThunk(
  actionTypes.FETCH_EVENT_LEAF_OCCURRENCES,
  async (event: Event) => await fetchEventLeafOccurrencesApi(event),
);

export const updateEventOccurrence = createAsyncThunk(
  actionTypes.UPDATE_EVENT_OCCURRENCE,
  async (event: Event) => await updateEventOccurrenceApi(event),
);

export const createEventOccurrence = createAsyncThunk(
  actionTypes.CREATE_EVENT_OCCURRENCE,
  async (event: Event) => await createEventOccurrenceApi(event),
);

export const createDateWithTimesEventOccurrence = createAsyncThunk(
  actionTypes.CREATE_DATE_WITH_TIMES_EVENT_OCCURRENCE,
  async (event: Event) => await createDateWithTimesEventOccurrenceApi(event),
);

export const updateDateWithTimesEventOccurrence = createAsyncThunk(
  actionTypes.UPDATE_DATE_WITH_TIMES_EVENT_OCCURRENCE,
  async (event: Event) => await updateDateWithTimesEventOccurrenceApi(event),
);

export const deleteDateWithTimesEventOccurrence = createAsyncThunk(
  actionTypes.DELETE_DATE_WITH_TIMES_EVENT_OCCURRENCE,
  async (event: Event) => await deleteDateWithTimesEventOccurrenceApi(event),
);

export const fetchEventPromos = createAsyncThunk(
  actionTypes.FETCH_EVENT_PROMOS,
  async (eventId: string) => await fetchEventPromosApi(eventId),
);

export const createEventPromos = createAsyncThunk(
  actionTypes.CREATE_EVENT_PROMO,
  async (promo: any) => await createEventPromosApi(promo),
);

export const updateEventPromos = createAsyncThunk(
  actionTypes.UPDATE_EVENT_PROMO,
  async (promo: any) => await updateEventPromosApi(promo),
);

export const deleteEventPromo = createAsyncThunk(
  actionTypes.DELETE_EVENT_PROMO,
  async (promo: any) => await deleteEventPromoApi(promo),
);

export const updateEventWaitlist = createAsyncThunk(
  actionTypes.UPDATE_EVENT_WAITLIST,
  async (data: any) => await updateEventWaitlistApi(data),
);

export const notifyWaitlistSubscribers = createAsyncThunk(
  actionTypes.NOTIFY_WAITLIST_SUBSCRIBERS,
  async (data: any) => await notifyWaitlistSubscribersApi(data),
);
export const createPreapprovedEvent = createAsyncThunk(
  actionTypes.CREATE_PREAPPROVED_EVENT,
  async (data: any) => await createPreapprovedEventAPI(data),
);

export const submitPreapprovedEvent = createAsyncThunk(
  actionTypes.SUBMIT_PREAPPROVED_EVENT,
  async (data: any) => await submitPreapprovedEventAPI(data),
);

// Event Hosts Actions
export const fetchEventHosts = createAsyncThunk(
  actionTypes.FETCH_EVENT_HOSTS,
  async (params: { eventId: string; status?: 'accept' | 'pending' }) =>
    await fetchEventHostsApi(params.eventId, params.status),
);

export const createEventHost = createAsyncThunk(
  actionTypes.CREATE_EVENT_HOST,
  async (data: {
    eventId: string;
    memberId: string;
    hostData?: { active?: boolean; first_name?: string; last_name?: string };
  }) => await createEventHostApi(data),
);

export const updateEventHost = createAsyncThunk(
  actionTypes.UPDATE_EVENT_HOST,
  async (data: {
    eventId: string;
    memberId: string;
    hostData: { active?: boolean; first_name?: string; last_name?: string; role?: string };
  }) => await updateEventHostApi(data),
);

export const deleteEventHost = createAsyncThunk(
  actionTypes.DELETE_EVENT_HOST,
  async (data: { eventId: string; memberId: string }) => await deleteEventHostApi(data),
);

export const inviteTeamMemberAsHost = createAsyncThunk(
  actionTypes.INVITE_TEAM_MEMBER_AS_HOST,
  async (data: {
    siteId: string;
    eventId: string;
    memberData: { email: string; first_name: string; last_name: string; role: string };
  }) => await inviteTeamMemberAsHostApi(data),
);

export const addEventHostDirect = createAsyncThunk(
  actionTypes.ADD_EVENT_HOST_DIRECT,
  async (data: {
    eventId: string;
    hostData: { email: string; first_name: string; last_name: string; role: string };
  }) => await addEventHostDirectApi(data),
);

export const resendPendingHostInvitation = createAsyncThunk(
  actionTypes.RESEND_PENDING_HOST_INVITATION,
  async (data: { eventId: string; invitationId: string }) =>
    await resendPendingHostInvitationApi(data),
);

export const deletePendingHostInvitation = createAsyncThunk(
  actionTypes.DELETE_PENDING_HOST_INVITATION,
  async (data: { eventId: string; invitationId: string }) =>
    await deletePendingHostInvitationApi(data),
);

export const bulkUpdateBookingFee = createAsyncThunk(
  actionTypes.BULK_UPDATE_BOOKING_FEE,
  async (data: { eventId: string; bookingFee: number }) =>
    await bulkUpdateBookingFeeApi(data.eventId, data.bookingFee),
);
